import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Space,
  Typography,
  Modal,
  Form,
  Popconfirm,
  Tooltip,
  Card,
  Switch,
  Tag,
  Select,
} from "antd";
import {
  Mail,
  Plus,
  Search,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
} from "lucide-react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Option } = Select;

const AdminEmail = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [form] = Form.useForm();

  // Fetch emails from API
  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: "marketplace-admin/api/admin-emails/",
        method: "GET",
      });
      // Response is directly an array
      if (Array.isArray(response)) {
        setEmails(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setEmails(response.data);
      } else if (Array.isArray(response?.results)) {
        setEmails(response.results);
      } else {
        setEmails([]);
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      ShowToast("Failed to fetch emails", Severty.ERROR);
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  // Add new email
  const handleAddEmail = async (values) => {
    try {
      const response = await request({
        url: "marketplace-admin/api/admin-emails/create/",
        method: "POST",
        data: values,
      });
      if (response) {
        await fetchEmails();
        ShowToast("Email added successfully", Severty.SUCCESS);
        setIsModalVisible(false);
        form.resetFields();
        setEditingEmail(null);
      }
    } catch (error) {
      console.error("Error adding email:", error);
      ShowToast(error?.response?.data?.message || error?.message || "Failed to add email", Severty.ERROR);
    }
  };

  // Update existing email
  const handleUpdateEmail = async (values) => {
    try {
      const response = await request({
        url: `marketplace-admin/api/admin-emails/update/${editingEmail.id}/`,
        method: "PUT",
        data: values,
      });
      if (response) {
        await fetchEmails();
        ShowToast("Email updated successfully", Severty.SUCCESS);
        setIsModalVisible(false);
        form.resetFields();
        setEditingEmail(null);
      }
    } catch (error) {
      console.error("Error updating email:", error);
      ShowToast(error?.response?.data?.message || error?.message || "Failed to update email", Severty.ERROR);
    }
  };

  // Delete email
  const handleDelete = async (id) => {
    try {
      const response = await request({
        url: `marketplace-admin/api/admin-emails/delete/${id}/`,
        method: "DELETE",
      });
      if (response?.success || response?.status === 204 || response) {
        await fetchEmails();
        ShowToast("Email deleted successfully", Severty.SUCCESS);
      }
    } catch (error) {
      console.error("Error deleting email:", error);
      ShowToast(error?.response?.data?.message || error?.message || "Failed to delete email", Severty.ERROR);
    }
  };

  // Toggle status (active/inactive)
  const handleToggleStatus = async (record, checked) => {
    try {
      const newStatus = checked ? "active" : "inactive";
      const response = await request({
        url: `marketplace-admin/api/admin-emails/update/${record.id}/`,
        method: "PUT",
        data: { ...record, status: newStatus },
      });
      if (response) {
        await fetchEmails();
        ShowToast(`Email ${checked ? "activated" : "deactivated"} successfully`, Severty.SUCCESS);
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      ShowToast(error?.response?.data?.message || "Failed to update status", Severty.ERROR);
    }
  };

  const handleAddEdit = (values) => {
    if (editingEmail) {
      handleUpdateEmail(values);
    } else {
      handleAddEmail(values);
    }
  };

  const openModal = (record = null) => {
    if (record) {
      setEditingEmail(record);
      form.setFieldsValue({
        email: record.email,
        status: record.status || "active",
      });
    } else {
      setEditingEmail(null);
      form.setFieldsValue({
        status: "active",
      });
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // Get status tag
  const getStatusTag = (status) => {
    if (status === "active" || status === true) {
      return (
        <Tag icon={<CheckCircle size={12} />} color="success" style={{ borderRadius: "20px", padding: "2px 12px" }}>
          Active
        </Tag>
      );
    } else if (status === "inactive" || status === false) {
      return (
        <Tag icon={<XCircle size={12} />} color="default" style={{ borderRadius: "20px", padding: "2px 12px" }}>
          Inactive
        </Tag>
      );
    }
    return <Tag color="default">Unknown</Tag>;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <Space>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#3b82f6",
            }}
          >
            <Mail size={16} />
          </div>
          <Text strong style={{ color: "#1e293b", fontSize: "14px" }}>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {/* {getStatusTag(status)} */}
          <Switch
            checked={status === "active" || status === true}
            onChange={(checked) => handleToggleStatus(record, checked)}
            size="small"
          />
        </div>
      ),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => {
        if (!date) return <Text style={{ color: "#64748b", fontSize: "14px" }}>-</Text>;
        const formattedDate = new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        return <Text style={{ color: "#64748b", fontSize: "14px" }}>{formattedDate}</Text>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<Edit2 size={16} color="#3b82f6" />}
              onClick={() => openModal(record)}
              className="action-btn"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this email?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            placement="topRight"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                icon={<Trash2 size={16} color="#ef4444" />}
                className="action-btn-danger"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredEmails = emails.filter((item) => {
    const matchesSearch = item.email?.toLowerCase().includes(searchText.toLowerCase());
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && (item.status === "active" || item.status === true)) ||
      (statusFilter === "inactive" && (item.status === "inactive" || item.status === false));
    return matchesSearch && matchesStatus;
  });

  return (
    <div style={{ padding: "24px", minHeight: "100vh", background: "#f8fafc" }}>
      <Card
        bordered={false}
        style={{
          borderRadius: "16px",
          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "32px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>
              Admin Email Settings
            </Title>
            <Text style={{ color: "#64748b", fontSize: "14px" }}>
              Manage administrative email addresses for system notifications and access control.
            </Text>
          </div>
          <Button
            type="primary"
            icon={<Plus size={18} />}
            onClick={() => openModal()}
            style={{
              height: "44px",
              padding: "0 20px",
              borderRadius: "8px",
              background: "#2563eb",
              border: "none",
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
              boxShadow: "0 4px 10px rgba(37, 99, 235, 0.2)",
            }}
          >
            Add New Email
          </Button>
        </div>

        <div style={{ marginBottom: "24px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <Input
            placeholder="Search by email address..."
            prefix={<Search size={18} color="#94a3b8" />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: "300px",
              height: "44px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              background: "#fff",
            }}
          />
          <Select
            placeholder="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: "180px", height: "44px" }}
            className="status-filter"
          >
            <Option value="all">All Status</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={filteredEmails}
          loading={loading}
          rowKey="id"
          scroll={{ x: 968 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} emails`,
            style: { marginTop: "24px" },
          }}
          className="premium-table"
        />
      </Card>

      <Modal
        title={
          <div style={{ paddingBottom: "10px" }}>
            <Title level={4} style={{ margin: 0, fontWeight: 700 }}>
              {editingEmail ? "Edit Admin Email" : "Add New Admin Email"}
            </Title>
            <Text style={{ color: "#64748b", fontWeight: 400, fontSize: "14px" }}>
              {editingEmail ? "Update the details of this email address." : "Register a new email for admin notifications."}
            </Text>
          </div>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingEmail(null);
        }}
        footer={null}
        destroyOnClose
        centered
        width={500}
        bodyStyle={{ paddingTop: "20px" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddEdit}
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label={<Text strong style={{ color: "#334155" }}>Email Address</Text>}
            rules={[
              { required: true, message: "Please enter email address" },
              { type: "email", message: "Please enter a valid email address" },
            ]}
          >
            <Input
              placeholder="e.g. admin@yourdomain.com"
              style={{ height: "48px", borderRadius: "8px" }}
              prefix={<Mail size={16} color="#94a3b8" style={{ marginRight: "8px" }} />}
            />
          </Form.Item>

          {/* <Form.Item
            name="status"
            label={<Text strong style={{ color: "#334155" }}>Status</Text>}
            initialValue="active"
          >
            <Select
              placeholder="Select status"
              style={{ height: "48px", borderRadius: "8px" }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item> */}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "40px",
            }}
          >
            <Button
              onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
                setEditingEmail(null);
              }}
              style={{ height: "44px", borderRadius: "8px", fontWeight: 500, padding: "0 24px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                height: "44px",
                borderRadius: "8px",
                background: "#2563eb",
                fontWeight: 600,
                padding: "0 24px"
              }}
            >
              {editingEmail ? "Update Email" : "Add Email"}
            </Button>
          </div>
        </Form>
      </Modal>

      <style jsx>{`
        .premium-table :global(.ant-table) {
          background: transparent;
          border-radius: 12px;
        }
        .premium-table :global(.ant-table-thead > tr > th) {
          background: #f8fafc;
          color: #64748b;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e2e8f0;
          padding: 16px;
        }
        .premium-table :global(.ant-table-tbody > tr > td) {
          border-bottom: 1px solid #f1f5f9;
          padding: 16px;
        }
        .premium-table :global(.ant-table-row:hover > td) {
          background: #f1f5f9 !important;
        }
        .action-btn:hover {
          background: #eff6ff !important;
          border-radius: 6px;
        }
        .action-btn-danger:hover {
          background: #fef2f2 !important;
          border-radius: 6px;
        }
        .status-filter :global(.ant-select-selector) {
          border-radius: 10px !important;
          height: 44px !important;
          display: flex !important;
          align-items: center !important;
          border: 1px solid #e2e8f0 !important;
        }
        .status-filter :global(.ant-select-selection-item) {
          display: flex !important;
          align-items: center !important;
        }
      `}</style>
    </div>
  );
};

export default AdminEmail;